import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Sparkles,
  Download,
  Wand2,
  BadgeDollarSign
} from 'lucide-react';
import { DownloadProgress } from '../types';
import { DownloadProgressBar } from './DownloadProgressBar';
import { useAppStore } from '../stores/appStore';
import { useAuthStore } from '../stores/authStore';
import { signInWithGoogle, signOut } from '../services/auth';

interface NavbarProps {
  downloadProgress: DownloadProgress[];
}

export const Navbar: React.FC<NavbarProps> = ({ downloadProgress }) => {
  const location = useLocation();
  const { removeDownloadProgress } = useAppStore();

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/search', label: 'Search', icon: Search },
    { path: '/pricing', label: 'Pricing', icon: BadgeDollarSign},
    { path: '/ai-editor', label: 'AI Generate', icon: Wand2 },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Sparkles className="w-8 h-8 text-purple-600" />
              <span className="text-xl font-bold text-gray-900">App Icon Generate</span>
            </Link>
          </div>

          {/* 导航菜单 */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              if ((item as any).hidden) return null;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* 右侧：下载进度 + 登录 */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              {downloadProgress.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Download className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-gray-600">
                    {downloadProgress.length}
                  </span>
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 hidden group-hover:block">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900">Download Progress</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto p-4 space-y-3">
                      {downloadProgress.map((progress) => (
                        <DownloadProgressBar
                          key={progress.id}
                          progress={progress}
                          onRemove={removeDownloadProgress}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <NavAuthSection />
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      <div className="md:hidden border-t border-gray-200">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            if ((item as any).hidden) return null;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'text-purple-600'
                    : 'text-gray-600'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

const NavAuthSection: React.FC = () => {
  const { user } = useAuthStore();
  return (
    <div className="flex items-center space-x-3">
      {user ? (
        <>
          <span className="text-sm text-gray-700">{user.displayName || user.email}</span>
          <button onClick={() => signOut()} className="px-3 py-1 text-sm border rounded">Logout</button>
        </>
      ) : (
        <button onClick={() => signInWithGoogle()} className="px-3 py-1 text-sm bg-purple-600 text-white rounded">Login</button>
      )}
    </div>
  );
};
